"use client";

import { useState } from "react";
import type { TooltipContentProps } from "recharts";
import {
	Area,
	AreaChart,
	Bar,
	BarChart,
	CartesianGrid,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { cn } from "@/lib/utils";

const CHART_COLORS = [
	"var(--chart-1)",
	"var(--chart-2)",
	"var(--chart-3)",
	"var(--chart-4)",
	"var(--chart-5)",
];

interface ChartSeries {
	dataKey: string;
	name: string;
	color?: string;
}

interface ChartProps {
	/** Chart type */
	type: "line" | "bar" | "area";
	/** Data array - each object should have xKey and series dataKeys */
	data: Record<string, string | number>[];
	/** Key for the x-axis labels */
	xKey: string;
	/** Series to display - each maps to a dataKey in your data */
	series: ChartSeries[];
	/** Chart height in pixels */
	height?: number;
	/** Format Y-axis values as currency */
	formatCurrency?: boolean;
	/** Optional chart title */
	title?: string;
	className?: string;
}

function formatTick(value: number, formatCurrency?: boolean): string {
	if (formatCurrency) {
		if (Math.abs(value) >= 1_000_000) {
			return `$${(value / 1_000_000).toFixed(1)}M`;
		}
		if (Math.abs(value) >= 1_000) {
			return `$${(value / 1_000).toFixed(0)}k`;
		}
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			maximumFractionDigits: 0,
			minimumFractionDigits: 0,
		}).format(value);
	}
	if (Math.abs(value) >= 1_000_000) {
		return `${(value / 1_000_000).toFixed(1)}M`;
	}
	if (Math.abs(value) >= 1_000) {
		return `${(value / 1_000).toFixed(0)}k`;
	}
	return value.toString();
}

function formatTooltipValue(value: number, formatCurrency?: boolean): string {
	if (formatCurrency) {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			maximumFractionDigits: 0,
			minimumFractionDigits: 0,
		}).format(value);
	}
	return value.toLocaleString();
}

type MutedSeries = Partial<Record<string, true>>;

function TooltipBody({
	active,
	payload,
	label,
	formatCurrency,
	mutedKeys,
}: Pick<TooltipContentProps<number, string>, "active" | "payload" | "label"> & {
	formatCurrency: boolean;
	mutedKeys: MutedSeries;
}) {
	if (!active || payload == null || !Array.isArray(payload)) return null;
	const visiblePayload = payload.filter((item) => {
		const dk = item.dataKey;
		if (dk == null) return true;
		return !mutedKeys[String(dk)];
	});
	if (visiblePayload.length === 0) return null;

	return (
		<div
			style={{
				backgroundColor: "var(--card)",
				border: "1px solid var(--border)",
				borderRadius: "var(--radius)",
				fontSize: "0.875rem",
				padding: "0.625rem",
			}}
		>
			<p style={{ margin: "0 0 0.5rem", color: "var(--foreground)" }}>
				{typeof label === "number" ? String(label) : label}
			</p>
			<ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
				{visiblePayload.map((entry, i) => {
					const v =
						typeof entry.value === "number"
							? entry.value
							: Number(entry.value) || 0;
					return (
						<li key={entry.dataKey != null ? String(entry.dataKey) : i}>
							<span style={{ color: entry.color }}>
								{entry.name}: {formatTooltipValue(v, formatCurrency)}
							</span>
						</li>
					);
				})}
			</ul>
		</div>
	);
}

export function Chart({
	type,
	data,
	xKey,
	series,
	height = 300,
	formatCurrency = false,
	title,
	className,
}: ChartProps) {
	const ChartWrapper =
		type === "line" ? LineChart : type === "bar" ? BarChart : AreaChart;

	const [mutedKeys, setMutedKeys] = useState<MutedSeries>({});

	const margin = { top: 10, right: 30, left: 10, bottom: 0 };

	const toggleSeries = (dataKey: string) => {
		setMutedKeys((prev) =>
			prev[dataKey]
				? { ...prev, [dataKey]: undefined }
				: { ...prev, [dataKey]: true },
		);
	};

	const content = (
		<>
			<ResponsiveContainer width="100%" height={height}>
				<ChartWrapper data={data} margin={margin}>
					<CartesianGrid
						strokeDasharray="3 3"
						stroke="var(--border)"
						opacity={0.5}
					/>
					<XAxis
						dataKey={xKey}
						stroke="var(--muted-foreground)"
						fontSize={12}
						tickLine={false}
					/>
					<YAxis
						stroke="var(--muted-foreground)"
						fontSize={12}
						tickLine={false}
						tickFormatter={(v) => formatTick(v, formatCurrency)}
					/>
					<Tooltip
						content={(props) => (
							<TooltipBody
								active={props.active}
								payload={props.payload}
								label={props.label}
								formatCurrency={formatCurrency}
								mutedKeys={mutedKeys}
							/>
						)}
						cursor={{ stroke: "var(--border)" }}
						allowEscapeViewBox={{ x: true, y: true }}
					/>
					{series.map((s, i) => {
						const color = s.color ?? CHART_COLORS[i % CHART_COLORS.length];
						const hidden = mutedKeys[s.dataKey] === true;
						if (type === "line") {
							return (
								<Line
									key={s.dataKey}
									type="monotone"
									dataKey={s.dataKey}
									name={s.name}
									stroke={color}
									strokeWidth={2}
									dot={{ fill: color, strokeWidth: 0 }}
									activeDot={{ r: 4, strokeWidth: 2 }}
									hide={hidden}
								/>
							);
						}
						if (type === "bar") {
							return (
								<Bar
									key={s.dataKey}
									dataKey={s.dataKey}
									name={s.name}
									fill={color}
									radius={[4, 4, 0, 0]}
									hide={hidden}
								/>
							);
						}
						return (
							<Area
								key={s.dataKey}
								type="monotone"
								dataKey={s.dataKey}
								name={s.name}
								stroke={color}
								fill={color}
								fillOpacity={0.3}
								strokeWidth={2}
								hide={hidden}
							/>
						);
					})}
				</ChartWrapper>
			</ResponsiveContainer>
			<fieldset className="mt-3 min-w-0 border-0 border-border/70 border-t p-0 pt-3 text-muted-foreground">
				<legend className="sr-only">
					Toggle visibility of chart series · click to show or hide each line or
					bar.
				</legend>
				<div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs">
					{series.map((s, i) => {
						const color = s.color ?? CHART_COLORS[i % CHART_COLORS.length];
						const muted = mutedKeys[s.dataKey] === true;
						return (
							<button
								key={s.dataKey}
								type="button"
								onClick={() => toggleSeries(s.dataKey)}
								className={cn(
									"inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1 transition-colors",
									muted
										? "border-border/80 bg-muted/30 text-muted-foreground line-through decoration-muted-foreground/80 opacity-65"
										: "border-border/80 bg-card text-foreground hover:bg-muted/50",
								)}
								aria-pressed={!muted}
								aria-label={muted ? `Show ${s.name}` : `Hide ${s.name}`}
							>
								<span
									className="inline-block size-2 shrink-0 rounded-full"
									style={{ backgroundColor: color }}
									aria-hidden
								/>
								{s.name}
							</button>
						);
					})}
				</div>
			</fieldset>
		</>
	);

	return (
		<figure className={cn("not-prose my-6", className)}>
			{title && (
				<figcaption className="mb-3 text-center text-sm font-medium text-muted-foreground">
					{title}
				</figcaption>
			)}
			<div
				className="rounded-lg border border-border bg-card p-4"
				style={{ minHeight: height }}
			>
				{content}
			</div>
		</figure>
	);
}
